import React from 'react';

import { CarePlanGoal, CarePlanNeed, CarePlanServiceLine } from '~clients/models';

import { bySequence } from '../reorder';

interface ComponentProps {
  goals: CarePlanGoal[];
  serviceLines: CarePlanServiceLine[];
  /** Resolves a goal's linked need → its display name for the chip label. */
  needsById: Map<string, CarePlanNeed>;
}

/**
 * Goal-coverage summary: one chip per goal with a green/red dot (has services or not),
 * the linked-need name (or goal text), and the service count. Renders nothing without
 * goals — the tab shows the "add a goal first" banner above the toolbar instead.
 */
const CoverageBar: React.FC<ComponentProps> = ({ goals, serviceLines, needsById }) => {
  if (goals.length === 0) {
    return null;
  }

  return (
    <div className="care-plan-coverage-bar" data-cy="care-plan-coverage-bar">
      {bySequence(goals).map((goal) => {
        const count = serviceLines.filter((line) => line.goal_id === goal.id).length;
        const hasServices = count > 0;
        const need = goal.need_id ? needsById.get(goal.need_id) : undefined;
        const label = need?.title || need?.description || goal.description;

        return (
          <span
            key={goal.id}
            className="care-plan-coverage-item"
            data-cy={`care-plan-coverage-item-${goal.id}`}
            title={`${label}: ${count} ${count === 1 ? 'service' : 'services'}`}
          >
            <span
              className={`care-plan-coverage-dot ${
                hasServices ? 'care-plan-coverage-dot--covered' : 'care-plan-coverage-dot--uncovered'
              }`}
              data-cy={`care-plan-coverage-dot-${goal.id}`}
              aria-hidden
            />
            <span className="visually-hidden">{hasServices ? 'Covered:' : 'No services:'}</span>
            <span className="care-plan-coverage-label">{label}</span>
            <span className="care-plan-coverage-count text-muted">({count})</span>
          </span>
        );
      })}
    </div>
  );
};

export default CoverageBar;
